import { fabric } from "fabric";
import { FabricEvent } from "../types/FabricCustomType";
import { Icons } from '../enums/SeatIcon'
import { ObjectSuperTypesEnum, ObjectTypesEnum } from "../enums";
import service from "../service";

/**
 * toObject util
 * @param {*} obj
 * @param {string[]} propertiesToInclude
 * @param {{ [key: string]: any }} [properties]
 */

const validObjects = [
  ObjectSuperTypesEnum.RectTable,
  ObjectSuperTypesEnum.RoundTable,
  ObjectSuperTypesEnum.Row
]

export const toObject = (
  obj: any,
  propertiesToInclude: string[],
  properties?: { [key: string]: any }
) =>
  fabric.util.object.extend(
    obj.callSuper("toObject"),
    propertiesToInclude.reduce(
      (prev, property) =>
        Object.assign(prev, {
          [property]: obj.get(property),
        }),
      Object.assign({}, properties)
    )
  );

export const getSvgObject = (icon: string, size: number, fill: string) => {
  let svg: any;
  fabric.loadSVGFromString(icon, function (objects, options) {
    svg = fabric.util.groupSVGElements(objects, options);
    svg.set({
      fill,
      originX: "center",
      originY: "center",
      objectCaching: false,
      type: "svg",
      width: size-3,
      strokeUniform: true,
      hasControls: false,
      hasBorders: false,
      lockMovementX: true,
      lockMovementY: true,
      loadType: "svg",
      svg: icon,
    });
    svg._objects.forEach((path: any) => {
      path.set('fill', fill);
    });
  });

  return svg;
}

/**
  * Sube por la jerarquía de grupos hasta encontrar el row (circle/text/svg → seat → row).
  */
export const getObjectFromEvent = (event: FabricEvent<MouseEvent | TouchEvent>): any | null => {
  let cur: any = event.target as any;
  while (cur) {
    if (validObjects.includes(cur.superType)) {
      return cur;
    }
    cur = cur.group;
  }
  return null;
};

/**
 * Círculo: centro local (0,0) con arc(0,0,r) en Fabric. Escala del radio vía normas de columnas
 * de la matriz 2×3 (estable con rotación; qrDecompose fallaba en algunas inclinaciones).
 */
export const getCircleSceneHitRadius = (circle: any): { center: fabric.Point; r: number } => {
  circle.setCoords?.();
  const m = circle.calcTransformMatrix();
  const center = fabric.util.transformPoint(new fabric.Point(0, 0), m);
  const sx = Math.hypot(m[0], m[1]);
  const sy = Math.hypot(m[2], m[3]);
  const scale = Math.max(sx, sy) || 1;
  const baseR =
    typeof circle.radius === "number" && circle.radius > 0
      ? circle.radius * scale
      : Math.max(circle.getScaledWidth(), circle.getScaledHeight()) / 2;
  return { center, r: baseR + 0 };
};

export const sceneCircleHit = (scene: fabric.Point, circle: any): boolean => {
  const { center, r } = getCircleSceneHitRadius(circle);
  return Math.hypot(scene.x - center.x, scene.y - center.y) <= r;
};

/**
 * Grupo seat (origin left/top): centro geométrico en locales (w/2, h/2).
 */
export const sceneSeatGroupHit = (scene: fabric.Point, seat: any): boolean => {
  seat.setCoords?.();
  const m = seat.calcTransformMatrix();
  const w = Number(seat.width || 0);
  const h = Number(seat.height || 0);
  const center = fabric.util.transformPoint(new fabric.Point(w / 2, h / 2), m);
  const r = Math.hypot(w, h) / 2 + 0;
  return Math.hypot(scene.x - center.x, scene.y - center.y) <= r;
};

export const findSeatInRow = (canvas: fabric.Canvas, nativeEvent: Event, rowGroup: any): fabric.Object | null => {
  if (!rowGroup?._objects) {
    return null;
  }

  const scene = getScenePointer(canvas, nativeEvent);
  rowGroup.setCoords?.();

  const seats = rowGroup._objects.filter(
    (o: any) => o.superType === ObjectSuperTypesEnum.Seat && o.visible !== false && o.evented !== false
  );
  if (!seats.length) {
    return null;
  }

  for (let i = seats.length - 1; i >= 0; i--) {
    const seat = seats[i] as any;
    seat.setCoords?.();
    const circle = seat.item ? seat.item(0) : null;

    if (circle && (circle.type === ObjectTypesEnum.Circle || circle.type === ObjectTypesEnum.Ellipse)) {
      if (sceneCircleHit(scene, circle)) {
        return seat;
      }
    } else if (sceneSeatGroupHit(scene, seat)) {
      return seat;
    }
  }

  return null;
};

/**
 * En secciones en cuña / columnas diagonales, los bounding boxes de varias filas se solapan:
 * Fabric puede dejar como target un "row" vecino. Si no hay hit en ese row, buscamos el círculo
 * más cercano al puntero entre todos los rows (misma escena que el render).
 */
export const findClosestSeatGlobal = (canvas: fabric.Canvas, nativeEvent: Event): { seat: any; object: any } | null => {
  const scene = getScenePointer(canvas, nativeEvent);
  const objects = canvas
    .getObjects()
    .filter((o: any) => validObjects.includes(o.superType)) as any[];

  let best: { seat: any; object: any; dist: number } | null = null;

  for (let ri = 0; ri < objects.length; ri++) {
    const objectGroup = objects[ri];
    if (!objectGroup._objects) {
      continue;
    }
    objectGroup.setCoords?.();
    const seats = objectGroup._objects.filter(
      (o: any) => o.superType === ObjectSuperTypesEnum.Seat
    );
    for (let si = 0; si < seats.length; si++) {
      const seat = seats[si] as any;
      const circle = seat.item ? seat.item(0) : null;
      if (!circle || (circle.type !== ObjectTypesEnum.Circle && circle.type !== ObjectTypesEnum.Ellipse)) {
        continue;
      }
      const { center, r } = getCircleSceneHitRadius(circle);
      const dist = Math.hypot(scene.x - center.x, scene.y - center.y);
      if (dist <= r && (!best || dist < best.dist)) {
        best = { seat, object: objectGroup, dist };
      }
    }
  }

  return best ? { seat: best.seat, object: best.object } : null;
};

export const resolveObjectsFromPointer = (
  canvas: fabric.Canvas,
  nativeEvent: Event,
  objectFromTarget: any | null
): { seat: any; object: any } | null => {
  if (objectFromTarget?._objects) {
    const local = findSeatInRow(canvas, nativeEvent, objectFromTarget);
    if (local) {
      return { seat: local, object: objectFromTarget };
    }
  }
  return findClosestSeatGlobal(canvas, nativeEvent);
};

export const enrichObjectEvent = (
  canvas: fabric.Canvas,
  event: FabricEvent<MouseEvent | TouchEvent>
): FabricEvent<MouseEvent | TouchEvent> => {
  const nativeEvent = "touches" in event.e ? event.e.touches[0] : event.e;
  const objectFromTarget = getObjectFromEvent(event);
  const resolvedObjets = resolveObjectsFromPointer(canvas, nativeEvent as Event, objectFromTarget);

  if (!resolvedObjets) {
    if (objectFromTarget) {
      return { ...event, target: objectFromTarget };
    }
    return event;
  }

  const { seat: foundSeat, object: objectForSelector } = resolvedObjets;
  const subTargets = [...(event.subTargets || [])];
  if (!subTargets.some((st: any) => st?.id === (foundSeat as any)?.id)) {
    subTargets.unshift(foundSeat as any);
  }

  return {
    ...event,
    target: objectForSelector,
    subTargets,
  };
};


export const wasDraggedForSelection = (
  opt: FabricEvent,
  isMultipleSelectionEnabled: boolean,
  startX: number,
  startY: number
): boolean => {
  if (!isMultipleSelectionEnabled || !opt.e) {
    return false;
  }
  const eventE = opt.e as MouseEvent | TouchEvent;
  const point = "touches" in eventE ? (eventE as TouchEvent).changedTouches[0] : eventE as MouseEvent;

  // Calculamos el desplazamiento desde el mousedown
  const dx = Math.abs(point.clientX - startX);
  const dy = Math.abs(point.clientY - startY);

  // Si se movió más de 2 pixeles consideramos que dibujó un recuadro
  return (dx > 2 || dy > 2);
};

export const getDraggingSelectionRect = (
  opt: FabricEvent,
  canvas: fabric.Canvas,
  startX: number,
  startY: number
): { minX: number; maxX: number; minY: number; maxY: number } | null => {
  if (!opt.e) return null;

  const canvasAny = canvas as any;

  // Limpiamos la caché interna de Fabric para forzar el recálculo
  canvasAny._absolutePointer = null;
  canvasAny._pointer = null;

  // Usamos startX y startY como origen real del drag
  const trueStartPoint = canvas.getPointer({
    clientX: startX,
    clientY: startY,
    type: 'mousedown'
  } as any);

  // Volvemos a limpiar la caché para calcular el endPoint real de este evento
  canvasAny._absolutePointer = null;
  canvasAny._pointer = null;
  const endPoint = canvas.getPointer(opt.e);

  const minX = Math.min(trueStartPoint.x, endPoint.x);
  const maxX = Math.max(trueStartPoint.x, endPoint.x);
  const minY = Math.min(trueStartPoint.y, endPoint.y);
  const maxY = Math.max(trueStartPoint.y, endPoint.y);

  return { minX, maxX, minY, maxY };
};


/**
 * Espacio de coordenadas "absolute" / aCoords (sin viewport): el mismo que usa
 * Object#calcCoords(true) y getBoundingRect(true). Debe coincidir con el círculo del asiento.
 */
export const getScenePointer = (canvas: fabric.Canvas, nativeEvent: Event): fabric.Point => {
  const c = canvas as fabric.Canvas & {
    restorePointerVpt: (p: fabric.Point) => fabric.Point;
  };
  const raw = c.getPointer(nativeEvent as MouseEvent, true);
  return c.restorePointerVpt(new fabric.Point(raw.x, raw.y));
}

export const createObjectModel = (parentKey: string, object: any) => {
  const objectModel = {
    categoryKey: object.category.key,
    ParentKey: parentKey,
    ObjectKey: object.id,
    ObjectValue: object.value,
  };
  return objectModel;
}

export const findObjectInCanvas = (canvas: any, parentKey: string, objectKey: string) => {
  const objects = canvas.getObjects();
  const parent = objects.find((s: any) => s.id == parentKey);
  if (!parent) return null;
  const group = parent as fabric.Group;
  if (!group._objects) return null;
  const object = group._objects.find((s: any) => s.id == objectKey);
  return {object , group};
}

export const updateObjectStatusInDb = async (objects: any, status: string, isSelector: boolean = false) => {
  const data = {
    Status: status,
    Objects: objects,
  };
  return await service.object.updateStatus(data, isSelector);
}